import LinkIcon from '@mui/icons-material/Link'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import SearchIcon from '@mui/icons-material/Search'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'

import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { ITikTokAccount, ITiktokProductsResult } from '../../../types/tiktok'
import { platformLinkKey } from '../../../utils/platform-link.util'

export interface ITiktokSkuOption {
  productId: string
  productName: string
  sellerSku: string
  skuId: string
  shopId: string
  shopName: string
}

interface ITiktokProductSku {
  id: string
  seller_sku: string
}

interface ITiktokProduct {
  id: string
  name: string
  skus?: ITiktokProductSku[]
}

interface Props {
  label?: string
  // sku_id ที่เลือกค้างไว้ — ว่างเมื่อผู้เรียกล้างค่าหลังกดเพิ่มการผูก
  value?: string
  onSelect: (value: ITiktokSkuOption | null) => void
  // key ของ SKU ที่ถูกผูกไปแล้ว → ข้อความบอกว่าใครใช้อยู่ (ดู platformLinkKey)
  // ผูก SKU เดียวกับ 2 สินค้า = ตัดสต๊อกไม่ได้ทั้งคู่ (ambiguous) จึงปิดไม่ให้เลือก
  usedKeys?: Map<string, string>
}

const PAGE_SIZE = 100
const ROWS_PER_PAGE = 20
// เพดานกันร้านที่สินค้าหลักหมื่น — เกินนี้ค้นได้เฉพาะที่โหลดมาแล้ว
const MAX_PAGES = 30
const CACHE_TTL = 10 * 60 * 1000

const CACHE_PREFIX = 'goodorder:tiktok-products:'

interface ICacheEntry {
  products: ITiktokProduct[]
  total: number
  // ว่าง = โหลดครบทั้งร้านแล้ว
  nextPageToken: string
  fetchedAt: number
}

// TikTok คืนสินค้ามาทั้งก้อน (รูป ราคา คำอธิบาย) แต่ตารางใช้แค่ 4 field
// ตัดทิ้งตั้งแต่รับเข้ามาเพื่อให้ cache ไม่ชน quota ของ sessionStorage
const slimProduct = (product: any): ITiktokProduct => ({
  id: product?.id,
  name: product?.name,
  skus: (product?.skus || []).map((sku: any) => ({
    id: sku?.id,
    seller_sku: sku?.seller_sku,
  })),
})

// เก็บใน sessionStorage — รอด refresh อยู่จนกว่าจะปิดแท็บ
// (in-memory อย่างเดียวหายทุกครั้งที่ refresh ซึ่งหน้านี้โดนบ่อย)
const readCache = (shopId: string): ICacheEntry | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + shopId)
    if (!raw) {
      return null
    }
    const entry = JSON.parse(raw) as ICacheEntry
    if (Date.now() - entry.fetchedAt >= CACHE_TTL) {
      sessionStorage.removeItem(CACHE_PREFIX + shopId)
      return null
    }
    return entry
  } catch (e) {
    return null
  }
}

// เขียนพลาดไม่ใช่เรื่องคอขาดบาดตาย (quota เต็ม / โหมดส่วนตัว) — ทิ้ง cache รอบนั้นไป
// แต่ลองเคลียร์ของร้านอื่นก่อน เพราะมักเป็นสาเหตุที่พื้นที่เต็ม
const writeCache = (shopId: string, entry: ICacheEntry): void => {
  const key = CACHE_PREFIX + shopId
  const payload = JSON.stringify(entry)
  try {
    sessionStorage.setItem(key, payload)
  } catch (e) {
    try {
      Object.keys(sessionStorage)
        .filter((name) => name.startsWith(CACHE_PREFIX) && name !== key)
        .forEach((name) => sessionStorage.removeItem(name))
      sessionStorage.setItem(key, payload)
    } catch (retryError) {
      // ปล่อยผ่าน — ไม่มี cache ก็แค่โหลดใหม่รอบหน้า
    }
  }
}

// เลือกสินค้า TikTok จากตารางแทน dropdown — ร้านที่มีสินค้าหลักร้อยรายการ
// dropdown โหลดได้แค่หน้าแรก ทำให้สินค้าที่เหลือหาไม่เจอเลย
const TiktokProductPicker = (props: Props) => {
  const {
    label = 'เลือกสินค้า TikTok เพื่อผูก',
    value,
    onSelect,
    usedKeys,
  } = props
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [shopId, setShopId] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const [selected, setSelected] = useState<ITiktokSkuOption | null>(null)
  const [page, setPage] = useState<number>(1)
  const [products, setProducts] = useState<ITiktokProduct[]>([])
  const [total, setTotal] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<string>('')
  const [reachedCap, setReachedCap] = useState<boolean>(false)
  // ปิด modal หรือสลับร้านระหว่างโหลด → รอบที่ค้างอยู่ต้องหยุดเขียน state
  const loadRunRef = useRef<number>(0)

  const { data: accounts, isLoading: accountsLoading } = useAxios<
    ITikTokAccount[]
  >({
    method: 'GET',
    url: '/tiktok/merchant/accounts',
  })

  // auto-pick บัญชีแรกเมื่อ accounts โหลดเสร็จ
  useEffect(() => {
    if (!shopId && accounts && accounts.length > 0) {
      setShopId(accounts[0].shopId)
    }
  }, [accounts, shopId])

  // ผู้เรียกล้างค่าหลังกดเพิ่มการผูก — ล้างที่โชว์ในปุ่มตามไปด้วย
  useEffect(() => {
    if (!value) {
      setSelected(null)
    }
  }, [value])

  // โหลดหน้าแรกมาโชว์ก่อน แล้วไล่ดึงหน้าที่เหลือต่อในพื้นหลังจนครบ
  // ค้นหาจึงครอบคลุมทั้งร้านได้ ทั้งที่ TikTok ไม่มี API ค้นด้วยชื่อสินค้า
  useEffect(() => {
    if (!isOpen || !shopId) {
      return
    }

    const runId = loadRunRef.current + 1
    loadRunRef.current = runId
    const isStale = () => loadRunRef.current !== runId

    const cached = readCache(shopId)

    let collected = cached ? cached.products : []
    let token = cached ? cached.nextPageToken : ''

    setLoadError('')
    setReachedCap(false)
    setProducts(collected)
    setTotal(cached ? cached.total : 0)

    if (cached && !token) {
      setIsLoading(false)
      return
    }

    const load = async () => {
      setIsLoading(true)
      const startedAt = Date.now()
      try {
        for (let fetched = 0; fetched < MAX_PAGES; fetched += 1) {
          const res = await axiosInstance.get<
            ITiktokProductsResult<ITiktokProduct>
          >('/tiktok/products', {
            params: {
              shopId,
              pageSize: PAGE_SIZE,
              ...(token ? { pageToken: token } : {}),
            },
          })
          if (isStale()) {
            return
          }

          collected = [
            ...collected,
            ...(res.data?.products ?? []).map(slimProduct),
          ]
          token = res.data?.nextPageToken ?? ''
          const count = res.data?.total ?? collected.length

          setProducts(collected)
          setTotal(count)
          writeCache(shopId, {
            products: collected,
            total: count,
            nextPageToken: token,
            fetchedAt: startedAt,
          })

          if (!token) {
            return
          }
        }
        setReachedCap(true)
      } catch (e) {
        if (!isStale()) {
          setLoadError('โหลดรายการสินค้าไม่สำเร็จ')
        }
      } finally {
        if (!isStale()) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      loadRunRef.current += 1
    }
  }, [isOpen, shopId])

  // แตก products[].skus[] ออกเป็นแถวระดับ SKU
  // พ่วง shopName มาด้วยเพื่อให้ผู้เรียกเก็บชื่อร้านลง platformLinks ได้ (id ล้วนอ่านไม่ออก)
  const rows = useMemo<ITiktokSkuOption[]>(() => {
    const shopName =
      accounts?.find((account) => account.shopId === shopId)?.sellerName ?? ''
    return products.flatMap((product) =>
      (product.skus || []).map((sku) => ({
        productId: product.id,
        productName: product.name,
        sellerSku: sku.seller_sku,
        skuId: sku.id,
        shopId,
        shopName,
      })),
    )
  }, [products, shopId, accounts])

  const matchedRows = useMemo<ITiktokSkuOption[]>(() => {
    const search = keyword.trim().toLowerCase()
    if (!search) {
      return rows
    }
    return rows.filter(
      (row) =>
        row.productName?.toLowerCase().includes(search) ||
        row.sellerSku?.toLowerCase().includes(search),
    )
  }, [rows, keyword])

  const pageCount = Math.max(Math.ceil(matchedRows.length / ROWS_PER_PAGE), 1)
  const currentPage = Math.min(page, pageCount)
  const firstIndex = (currentPage - 1) * ROWS_PER_PAGE
  const visibleRows = matchedRows.slice(firstIndex, firstIndex + ROWS_PER_PAGE)

  const usedBy = (row: ITiktokSkuOption): string | undefined => {
    const key = platformLinkKey({ skuId: row.skuId, sku: row.sellerSku })
    return key ? usedKeys?.get(key) : undefined
  }

  const handleOpen = () => {
    setIsOpen(true)
    setKeyword('')
    setPage(1)
  }

  const handlePick = (row: ITiktokSkuOption) => {
    setSelected(row)
    onSelect(row)
    setIsOpen(false)
  }

  const isFirstLoad = isLoading && rows.length === 0

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <>
              <Typography variant="body2" fontWeight={500}>
                {selected.productName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                seller_sku: {selected.sellerSku || '-'} · sku_id:{' '}
                {selected.skuId} · ร้าน: {selected.shopName || selected.shopId}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={handleOpen}
          sx={{ flexShrink: 0 }}
        >
          {selected ? 'เปลี่ยนสินค้า' : 'เลือกสินค้า'}
        </Button>
      </Stack>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>เลือกสินค้า TikTok</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {accounts && accounts.length > 1 && (
              <TextField
                select
                fullWidth
                label="บัญชี TikTok"
                value={shopId}
                onChange={(e) => {
                  setShopId(e.target.value)
                  setPage(1)
                }}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.shopId} value={account.shopId}>
                    {account.sellerName}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              placeholder="ค้นหาชื่อสินค้า หรือ seller_sku"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value)
                setPage(1)
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {isLoading && rows.length > 0 && (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                กำลังโหลดข้อมูลสินค้าหน้าอื่นอยู่ — ได้แล้ว {products.length}{' '}
                จาก {total} รายการ ค้นหาจะครบทั้งร้านเมื่อโหลดเสร็จ
              </Alert>
            )}

            {reachedCap && (
              <Alert severity="warning">
                ร้านนี้มีสินค้าเยอะมาก โหลดมา {products.length} รายการแรก
                ที่เหลือค้นไม่เจอ — ใช้ช่องกรอก seller_sku เองแทน
              </Alert>
            )}

            {loadError && <Alert severity="error">{loadError}</Alert>}

            {accountsLoading || isFirstLoad ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : visibleRows.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={6}
              >
                {keyword.trim()
                  ? 'ไม่พบสินค้าที่ค้นหา'
                  : 'ไม่พบสินค้าในบัญชีนี้'}
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ชื่อสินค้า</TableCell>
                      <TableCell>seller_sku</TableCell>
                      <TableCell>sku_id</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.map((row) => {
                      const used = usedBy(row)
                      return (
                        <TableRow
                          key={row.skuId}
                          hover={!used}
                          sx={{ opacity: used ? 0.5 : 1 }}
                        >
                          <TableCell>{row.productName}</TableCell>
                          <TableCell>{row.sellerSku || '-'}</TableCell>
                          <TableCell>{row.skuId}</TableCell>
                          <TableCell align="right">
                            {used ? (
                              <Chip
                                size="small"
                                label={used}
                                icon={<LinkIcon />}
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handlePick(row)}
                              >
                                เลือก
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              py={1}
            >
              <Button
                startIcon={<NavigateBeforeIcon />}
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ก่อนหน้า
              </Button>
              <Typography variant="body2" color="text.secondary">
                {matchedRows.length
                  ? `แสดง ${firstIndex + 1}-${
                      firstIndex + visibleRows.length
                    } จาก ${matchedRows.length} รายการ`
                  : '-'}
              </Typography>
              <Button
                endIcon={<NavigateNextIcon />}
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= pageCount}
              >
                ถัดไป
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TiktokProductPicker
